import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { loadTaxData } from "./taxData";
import { useTheme } from "../../../contexts/ThemeContext";
import { theme } from "../../../theme";
import { TRANSITIONS } from "../../../constants";

const Container = styled.div`
  background-color: transparent;
  width: 100%;
`;

const Title = styled.h2`
  font-size: ${theme.typography.fontSizes.xl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${({ isDarkMode }) => isDarkMode ? theme.colors.neutral[100] : theme.colors.neutral[700]};
  margin-bottom: ${theme.spacing[4]};
`;

const YearSelect = styled.select`
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
  border-radius: ${theme.radii.md};
  background-color: ${({ isDarkMode }) => isDarkMode ? theme.colors.neutral[800] : theme.colors.surface.default};
  color: ${({ isDarkMode }) => isDarkMode ? theme.colors.neutral[100] : theme.colors.neutral[700]};
  font-size: ${theme.typography.fontSizes.xs};

  &:focus {
    outline: none;
    ring: 2px;
    ring-color: ${theme.colors.primary.main};
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  width: 100% !important;
`;

const Table = styled.table`
  width: 100% !important;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
  border-radius: ${theme.radii.lg};
  background-color: ${({ isDarkMode }) => isDarkMode ? theme.colors.neutral[900] : theme.colors.surface.default};
`;

const Thead = styled.thead`
  background-color: ${({ isDarkMode }) => isDarkMode ? theme.colors.neutral[800] : theme.colors.neutral[100]};
`;

const Th = styled.th`
  padding: ${theme.spacing[2]} ${theme.spacing[2]};
  text-align: left;
  font-size: ${theme.typography.fontSizes.xs};
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${({ isDarkMode }) => isDarkMode ? theme.colors.neutral[300] : theme.colors.neutral[700]};
  border-bottom: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
`;

const Tr = styled.tr`
  &:nth-child(even) {
    background-color: ${({ isDarkMode }) =>
    isDarkMode ? theme.colors.neutral[800] : theme.colors.neutral[50]};
  }
`;

const Td = styled.td`
  padding: ${theme.spacing[2]} ${theme.spacing[2]};
  font-size: ${theme.typography.fontSizes.xs};
  color: ${({ isDarkMode }) => isDarkMode ? theme.colors.neutral[300] : theme.colors.neutral[700]};
  border-bottom: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
  transition: ${TRANSITIONS.fast};
  
  ${({ highlight, isDarkMode }) =>
    highlight &&
    `
    background-color: ${isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
    color: ${isDarkMode ? theme.colors.neutral[100] : theme.colors.neutral[900]};
    font-weight: ${theme.typography.fontWeights.bold};
  `}

  &:first-child {
    color: ${({ isDarkMode }) =>
    isDarkMode ? theme.colors.primary.light : theme.colors.primary.main};
    font-weight: ${theme.typography.fontWeights.semibold};
  }
`;

const Disclaimer = styled.p`
  margin-top: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSizes.xs};
  color: ${({ isDarkMode }) => isDarkMode ? theme.colors.neutral[400] : theme.colors.neutral[500]};
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const formatBracketRange = (range) => {
  if (range.includes('or more')) {
    // Extract the number and add a plus sign
    const number = range.match(/\$[\d,]+/)[0];
    return `${number}+`;
  }
  // Replace " to " with " - "
  return range.replace(' to ', ' - ');
};

const TaxBracketInformation = ({
  defaultYear,
  hideYearDropdown = false,
  hideTitle = false,  // Add hideTitle prop
  contentOnly = false,
  highlightFilingStatus,
  highlightBracketRate,
  onYearChange,
  isDarkMode: parentIsDarkMode,  // Renamed to avoid conflict
}) => {
  const { isDarkMode = parentIsDarkMode } = useTheme();
  const currentYear = defaultYear || new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [taxBrackets, setTaxBrackets] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const { TAX_BRACKETS } = await loadTaxData();
      setTaxBrackets(TAX_BRACKETS);
      setAvailableYears(
        Object.keys(TAX_BRACKETS)
          .map(Number)
          .sort((a, b) => b - a),
      );
      setLoading(false);
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (availableYears.length && !taxBrackets[selectedYear]) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear, taxBrackets]);

  useEffect(() => {
    if (defaultYear && defaultYear !== selectedYear) {
      setSelectedYear(defaultYear);
    }
  }, [defaultYear]);

  const validYear = taxBrackets[selectedYear]
    ? selectedYear
    : availableYears[0];

  if (loading || !availableYears.length || !taxBrackets[validYear]) {
    return contentOnly ? (
      <div className={isDarkMode ? 'text-gray-300' : 'text-gray-600 dark:text-gray-400'}>Loading tax bracket information...</div>
    ) : (
      <Container isDarkMode={isDarkMode}>
        {!hideTitle && <Title isDarkMode={isDarkMode}>Tax Bracket Information</Title>}
        <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600 dark:text-gray-400'}>Loading tax bracket information...</p>
      </Container>
    );
  }

  const statusKeyMap = {
    Single: "single",
    "Married Filing Jointly": "married",
    "Head of Household": "head",
    "Married Filing Separately": "marriedseparate",
  };
  
  const highlightCol = statusKeyMap[highlightFilingStatus];

  const content = (
    <TableContainer>
      <Table isDarkMode={isDarkMode}>
        <Thead isDarkMode={isDarkMode}>
          <tr>
            <Th isDarkMode={isDarkMode}>Marginal Tax Rate</Th>
            <Th isDarkMode={isDarkMode}>Single Filers</Th>
            <Th isDarkMode={isDarkMode}>Married Filing Jointly</Th>
            <Th isDarkMode={isDarkMode}>Married Filing Separately</Th>
            <Th isDarkMode={isDarkMode}>Head of Household</Th>
          </tr>
        </Thead>
        <tbody>
          {taxBrackets[validYear].map((bracket, index) => {
            const isHighlightRow = bracket.rate === highlightBracketRate;
            const statusKey = statusKeyMap[highlightFilingStatus];
            
            return (
              <Tr key={index} isDarkMode={isDarkMode}>
                <Td 
                  highlight={isHighlightRow} 
                  isDarkMode={isDarkMode}
                >
                  {bracket.rate}
                </Td>
                <Td
                  highlight={statusKey === "single" && isHighlightRow}
                  isDarkMode={isDarkMode}
                >
                  {formatBracketRange(bracket.single)}
                </Td>
                <Td
                  highlight={statusKey === "married" && isHighlightRow}
                  isDarkMode={isDarkMode}
                >
                  {formatBracketRange(bracket.married)}
                </Td>
                <Td
                  highlight={statusKey === "marriedseparate" && isHighlightRow}
                  isDarkMode={isDarkMode}
                >
                  {formatBracketRange(bracket.marriedseparate)}
                </Td>
                <Td
                  highlight={statusKey === "head" && isHighlightRow}
                  isDarkMode={isDarkMode}
                >
                  {formatBracketRange(bracket.head)}
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
      <Disclaimer isDarkMode={isDarkMode}>
        * Tax brackets are subject to change. Please consult with a tax professional for the most current information.
      </Disclaimer>
    </TableContainer>
  );

  if (contentOnly) {
    return content;
  }

  const handleYearChange = (e) => {
    const newYear = Number(e.target.value);
    setSelectedYear(newYear);
    if (onYearChange) {
      onYearChange(newYear);
    }
  };

  return (
    <Container isDarkMode={isDarkMode}>
      {!contentOnly && !hideTitle && (
      <HeaderContainer>
        <Title isDarkMode={isDarkMode}>Tax Bracket Information</Title>
        {!hideYearDropdown && (
          <YearSelect
            value={validYear}
              onChange={handleYearChange}
            isDarkMode={isDarkMode}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </YearSelect>
        )}
      </HeaderContainer>
      )}
      {content}
    </Container>
  );
};

export default TaxBracketInformation;
